import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { signOut, onAuthStateChanged, User } from "firebase/auth";
import { auth } from "../config/firebase";
import { UserDetail } from "@/models";
import { getUserProfile } from "@/services";
import { ROUTE_PATHS } from "@/constants";
import {
  getStoredTenantId,
  setStoredTenantId,
  removeStoredTenantId,
} from "@/utils/helper/tenant-storage";
import { getStoredLiffId, setStoredLiffId } from "@/config/liff";
import { queryClient } from "@/utils/helper/react-query-client";
import { clearActiveBranchId } from "@/utils/branchStore";
import {
  isNetworkLevelFailure,
  isPageNavigatingAway,
  markPageNavigatingAway,
  reportError,
  reportRequestError,
  setErrorReportUser,
} from "@/utils/helper/error-reporter";
import { config } from "@/constants";

interface AuthContextType {
  user: User | null;
  userDetail: UserDetail | null;
  token: string | null;
  loading: boolean;
  stalled: boolean;
  error: string | null;
  logout: () => Promise<void>;
  retryProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userDetail: null,
  token: null,
  loading: true,
  stalled: false,
  error: null,
  logout: async () => {},
  retryProfile: async () => {},
});

// Watchdog for the auth bootstrap. If `loading` hasn't dropped within this
// window — onAuthStateChanged never firing, getIdToken or the profile request
// hanging on a dead connection — consumers get `stalled: true` and must offer
// a reload instead of an eternal "Loading...". Longer than the axios timeout
// so a slow-but-answering backend surfaces as a profile `error` (retryable
// in place) rather than a stall.
const AUTH_STALL_TIMEOUT_MS = 30_000;

// Cap on the boot-time hostname→tenant lookup. Cold starts block first paint
// on this call, so it must be short: past this the app falls back to whatever
// tenant is cached (or the tenant-not-found screen) rather than holding the
// splash on a dead connection.
const TENANT_RESOLVE_TIMEOUT_MS = 5_000;
const TENANT_RESOLVE_REPORT_DEFER_MS = 2_000;

// The backend rejects every authenticated call with 403 "Tenant not found" /
// "Tenant database not configured" when the request carries no x-tenant-id
// header or the master tenant_users mapping is missing. Such a session can
// never recover on its own, so we force a clean re-login (which also lets the
// backend self-heal a missing mapping). Newer backends attach a stable
// TENANT_* code; older ones only have the message.
const isTenantForbiddenError = (error: unknown): boolean => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { message?: unknown; code?: unknown };
      };
    }
  )?.response;
  if (!response) return false;
  const code = response.data?.code;
  if (typeof code === "string" && code.startsWith("TENANT_")) return true;
  if (response.status !== 403) return false;
  const message = response.data?.message;
  return (
    typeof message === "string" && message.toLowerCase().includes("tenant")
  );
};

// QTY-1697: the backend rejects every member-facing call from an INACTIVE
// member with this stable code. Such a session can never recover either, so
// force a re-login; LoginPage surfaces the message via ?error=member_inactive.
const isMemberInactiveError = (error: unknown): boolean => {
  const response = (error as { response?: { data?: { code?: unknown } } })
    ?.response;
  return response?.data?.code === "MEMBER_INACTIVE";
};

// The permission guard answers 403 "User not found" when the Firebase account
// has no member record in this tenant (deleted member, account from another
// club). Nothing on the device can fix that, and without a forced re-login
// the member bounced /callback → /member → /callback on every open
// (2026-10-06, wellfit: three loops in 20 s). Same treatment as INACTIVE.
const isUserNotFoundError = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: number; data?: { message?: unknown } } }
  )?.response;
  // `includes`, like the tenant check above: an exact match would silently
  // disable this re-login on any backend wording change ("User not found.",
  // "User not found in tenant") and bring the bounce loop back. The proper
  // signal is a stable `code` from the backend (see tenant-context.error.ts).
  return (
    response?.status === 403 &&
    typeof response.data?.message === "string" &&
    response.data.message.toLowerCase().includes("user not found")
  );
};

// A network-level failure that arrives FAST — connection reset, a Cloudflare
// error page without CORS headers, a DNS blip — is usually transient, so the
// bootstrap retries it once after a short pause instead of sending the member
// straight to the retry screen. A 20 s timeout is neither fast nor transient
// and is not retried: a second attempt would run past the 30 s stall watchdog.
const FAST_FAILURE_MAX_MS = 5_000;
const NETWORK_RETRY_DELAY_MS = 1_500;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });

const withOneNetworkRetry = async <T,>(attempt: () => Promise<T>): Promise<T> => {
  const startedAt = Date.now();
  try {
    return await attempt();
  } catch (error) {
    const failedFast = Date.now() - startedAt <= FAST_FAILURE_MAX_MS;
    if (
      !failedFast ||
      !isNetworkLevelFailure(error, { strict: true }) ||
      isPageNavigatingAway()
    ) {
      throw error;
    }
    await wait(NETWORK_RETRY_DELAY_MS);
    return await attempt();
  }
};

// Which step of the bootstrap the stall watchdog caught. "auth-state" is
// Firebase's own init (persistence read, and for a stored session one
// accounts:lookup round-trip to Google with a 30–60 s timeout); the others
// are ours. `hasFirebaseUser` alone could not tell these apart: currentUser
// stays null until Firebase finishes, even when a session is stored.
type BootPhase = "auth-state" | "id-token" | "profile";

// Firebase keeps the signed-in user under this key in IndexedDB
// (db firebaseLocalStorageDb / store firebaseLocalStorage) or, when IndexedDB
// is unavailable, localStorage. Reading it ourselves tells a stall on a cold
// device ("none") from one on a returning member ("indexeddb"/"localstorage"),
// and a hung IndexedDB ("timeout") from a slow network. Diagnostic only.
const SESSION_PROBE_TIMEOUT_MS = 1_500;
type StoredSession =
  | "indexeddb"
  | "localstorage"
  | "none"
  | "timeout"
  | "unreadable";

const storedSessionKey = (): string =>
  `firebase:authUser:${auth.config.apiKey}:${auth.name}`;

const readIndexedDbSession = (): Promise<boolean> =>
  new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open("firebaseLocalStorageDb", 1);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("blocked"));
    request.onupgradeneeded = () => {
      // The DB did not exist: no session here. Abort so we don't create it.
      request.transaction?.abort();
      resolve(false);
    };
    request.onsuccess = () => {
      const db = request.result;
      try {
        const store = db
          .transaction("firebaseLocalStorage", "readonly")
          .objectStore("firebaseLocalStorage");
        const get = store.get(storedSessionKey());
        get.onsuccess = () => {
          db.close();
          resolve(Boolean(get.result?.value));
        };
        get.onerror = () => {
          db.close();
          reject(get.error);
        };
      } catch (e) {
        db.close();
        reject(e);
      }
    };
  });

const describeStoredSession = async (): Promise<StoredSession> => {
  try {
    if (globalThis.localStorage?.getItem(storedSessionKey())) {
      return "localstorage";
    }
  } catch {
    // locked-down webview: fall through to IndexedDB
  }
  if (!globalThis.indexedDB) return "none";
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const found = await Promise.race([
      readIndexedDbSession(),
      new Promise<"timeout">((resolve) => {
        timer = setTimeout(() => resolve("timeout"), SESSION_PROBE_TIMEOUT_MS);
      }),
    ]);
    if (found === "timeout") return "timeout";
    return found ? "indexeddb" : "none";
  } catch {
    return "unreadable";
  } finally {
    clearTimeout(timer);
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Preserve the tenant so a fresh login targets the same club, then hard
  // redirect to /login. Best-effort: never throw.
  const forceReLogin = useCallback(
    async (destination?: string): Promise<void> => {
      // Clear the active branch before signOut so the re-seed effect can't read
      // a stale value if it flushes between the auth listener and the clears
      // below (this path hard-navigates afterwards, but keep it order-safe too).
      clearActiveBranchId();
      setErrorReportUser(null);
      try {
        await signOut(auth);
      } catch {
        // ignore — we're abandoning this session either way
      }
      try {
        // Drop cached member data so the next member on this device can't see
        // the previous member's (non-member-keyed) query results.
        queryClient.clear();
      } catch {
        // ignore
      }
      try {
        const tenantId = getStoredTenantId();
        const liffId = getStoredLiffId();
        globalThis.localStorage.clear();
        if (tenantId) {
          setStoredTenantId(tenantId);
        }
        // Club config, not member data: without it the /login page we are
        // about to load cannot init LIFF (INVALID_CONFIG) and the OTP login
        // dead-ends with no way to resolve the LINE user id.
        if (liffId) {
          setStoredLiffId(liffId);
        }
      } catch {
        // ignore
      }
      // Reset the active branch so the next member on this device doesn't
      // inherit it (localStorage.clear() can't reach the in-memory copy).
      clearActiveBranchId();
      setErrorReportUser(null);
      markPageNavigatingAway();
      globalThis.location.href = destination ?? ROUTE_PATHS.AUTH.LOGIN;
    },
    [],
  );

  const loadUserProfile = useCallback(async (): Promise<void> => {
    try {
      const userProfile = await withOneNetworkRetry(() => getUserProfile());
      if (!mountedRef.current) return;
      setUserDetail(userProfile);
      setErrorReportUser(userProfile?.id);
      setError(null);
    } catch (e) {
      // Stale-tenant sessions can never recover — force a clean re-login.
      if (isTenantForbiddenError(e)) {
        // 403 → WARNING by status; the stage/outcome say it forced a re-login.
        reportRequestError(e, { stage: "profile", outcome: "tenant-forbidden-relogin" });
        await forceReLogin();
        return;
      }
      if (isMemberInactiveError(e)) {
        await forceReLogin(`${ROUTE_PATHS.AUTH.LOGIN}?error=member_inactive`);
        return;
      }
      if (isUserNotFoundError(e)) {
        reportRequestError(e, { stage: "profile", outcome: "user-not-found-relogin" });
        await forceReLogin(`${ROUTE_PATHS.AUTH.LOGIN}?error=user_not_found`);
        return;
      }
      if (!mountedRef.current) return;
      // The member is about to see the retry screen; make sure we see it too.
      reportRequestError(e, { stage: "profile" });
      // Surface the failure instead of silently rendering private pages with
      // a null profile. Consumers can show this and call retryProfile().
      setUserDetail(null);
      setError("ไม่สามารถโหลดข้อมูลสมาชิกได้ กรุณาลองใหม่อีกครั้ง");
    }
  }, [forceReLogin]);

  useEffect(() => {
    // Reset for StrictMode's dev-only mount → cleanup → mount cycle.
    mountedRef.current = true;
    ensureTenantId();
    let unsubscribe: (() => void) | null = null;

    // Anything in the bootstrap below that never settles would otherwise
    // leave `loading` true forever with no signal to the user. The timer only
    // ever fires while the gate is still up: a completed bootstrap clears it.
    let bootPhase: BootPhase = "auth-state";
    const stallTimer = globalThis.setTimeout(() => {
      if (!mountedRef.current) return;
      setStalled(true);
      const phase = bootPhase;
      const hasFirebaseUser = !!auth.currentUser;
      // The session probe is bounded (1.5 s) and never throws; the report
      // waits for it so one log line says where the boot hung and for whom.
      void describeStoredSession().then((storedSession) => {
        reportError("Auth bootstrap stalled", {
          extra: {
            stage: "bootstrap",
            timeoutMs: AUTH_STALL_TIMEOUT_MS,
            hasFirebaseUser,
            phase,
            storedSession,
          },
        });
      });
    }, AUTH_STALL_TIMEOUT_MS);

    const handleUser = async (firebaseUser: User | null): Promise<void> => {
      if (!mountedRef.current) return;
      try {
        setUser(firebaseUser);
        if (firebaseUser) {
          ensureTenantId();
          bootPhase = "id-token";
          const idToken = await withOneNetworkRetry(() =>
            firebaseUser.getIdToken(),
          );
          if (!mountedRef.current) return;
          setToken(idToken);
          // Load the profile before we drop the loading gate so pages mount
          // with the member in state. Does not reject: profile failure is
          // captured as error / forceReLogin inside loadUserProfile.
          bootPhase = "profile";
          await loadUserProfile();
        } else {
          setToken(null);
          setUserDetail(null);
          setError(null);
        }
      } catch (e) {
        // getIdToken (or anything else above) failed — surface it instead of
        // silently rendering private pages with a null profile.
        reportError(e, { extra: { stage: "session-restore" } });
        if (mountedRef.current) {
          setError("ไม่สามารถกู้คืนเซสชันได้ กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        globalThis.clearTimeout(stallTimer);
        if (mountedRef.current) {
          setStalled(false);
          setLoading(false);
        }
      }
    };

    // Wait until Firebase has fully restored the persisted session.
    // Registering onAuthStateChanged after authStateReady guarantees the first
    // callback fires immediately with the known state — no grace period needed.
    // If auth.currentUser is null here, the user is definitively unauthenticated.
    void auth.authStateReady().then(() => {
      if (!mountedRef.current) return;
      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        void handleUser(firebaseUser);
      });
    });

    return () => {
      mountedRef.current = false;
      globalThis.clearTimeout(stallTimer);
      unsubscribe?.();
    };
  }, [loadUserProfile]);

  const logout = useCallback(async () => {
    // Reset the active branch BEFORE signOut. signOut fires the auth listener
    // (→ uid change → LineBranchProvider re-seed effect); clearing first makes
    // that effect read the reset value regardless of scheduling, instead of
    // relying on React batching the listener's setState into a later task.
    clearActiveBranchId();
    setErrorReportUser(null);
    await signOut(auth);
    // Clear cached member data so the next member on this device can't see the
    // previous member's (non-member-keyed) query results.
    queryClient.clear();
    const tenantId = getStoredTenantId();
    const liffId = getStoredLiffId();
    globalThis.localStorage.clear();
    if (tenantId) {
      setStoredTenantId(tenantId);
    }
    if (liffId) {
      setStoredLiffId(liffId);
    }
    // localStorage.clear() above also drops the stored branch key; re-assert
    // the in-memory reset since clear() can't reach the module copy.
    clearActiveBranchId();
    setErrorReportUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({
          user,
          userDetail,
          token,
          loading,
          stalled,
          error,
          logout,
          retryProfile: loadUserProfile,
        }),
        [
          user,
          userDetail,
          token,
          loading,
          stalled,
          error,
          logout,
          loadUserProfile,
        ],
      )}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Returns true when the tenant came from a URL param on THIS page load —
// main.tsx uses that to skip hostname resolution (dev/QA tenant switching).
export const ensureTenantId = (): boolean => {
  try {
    const searchParams = new URLSearchParams(globalThis.location.search);
    let tenantIdFromQuery =
      searchParams.get("tanantId") || searchParams.get("tenantId");

    if (!tenantIdFromQuery) {
      const liffState = searchParams.get("liff.state");
      if (liffState) {
        try {
          const liffStateParams = new URLSearchParams(
            liffState.startsWith("?") ? liffState : `?${liffState}`,
          );
          tenantIdFromQuery =
            liffStateParams.get("tanantId") || liffStateParams.get("tenantId");
        } catch {
          // no-op
        }
      }
    }

    if (tenantIdFromQuery) {
      setStoredTenantId(tenantIdFromQuery);
      return true;
    }

    // getStoredTenantId also restores the localStorage copy from the cookie
    // mirror when the LINE webview evicted localStorage.
    const stored = getStoredTenantId();
    if (stored) {
      // Re-assert both stores: seeds the cookie for sessions that predate
      // the mirror and refreshes its Max-Age on every visit.
      setStoredTenantId(stored);
      return false;
    }

    // No env fallback anymore: the tenant arrives from an entry-link param
    // (persisted above) or from resolveTenantIdFromHost(). main.tsx renders
    // TenantNotFoundScreen when nothing could be resolved.
    removeStoredTenantId();
    return false;
  } catch {
    // no-op for non-browser environments
    return false;
  }
};

// Asks the API which tenant owns this hostname and caches the answer.
// Plain fetch, not the axios instance: that one's interceptors attach
// auth/tenant headers and bounce to /login on any non-retryable error.
export const resolveTenantIdFromHost = async (): Promise<void> => {
  try {
    const apiUrl = config.BASE_API_URL;
    if (!apiUrl) return;

    // AbortController instead of AbortSignal.timeout() — LINE in-app webviews
    // on older Androids predate Chromium 103.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TENANT_RESOLVE_TIMEOUT_MS);
    try {
      const host = globalThis.location.hostname.toLowerCase();
      const response = await fetch(
        `${apiUrl.replace(/\/+$/, "")}/master/tenant-client/resolve?host=${encodeURIComponent(host)}`,
        // no-store: a host→tenant remap must take effect on the next open —
        // never let the webview's HTTP cache answer for the server.
        { signal: controller.signal, cache: "no-store" },
      );
      // 404 = unmapped host (localhost, dev site): keep link/cache behavior.
      if (!response.ok) return;

      const { tenantId } = await response.json();
      if (typeof tenantId === "string" && tenantId) {
        const previous = getStoredTenantId();
        // Server wins over a stale or crafted cache entry; the helper also
        // keeps the cookie mirror in sync.
        setStoredTenantId(tenantId);
        // A warm-start session already rendered with the previous tenant:
        // axios reads the header per request and React Query still holds the
        // old tenant's data, so a silent swap would mix tenants mid-session.
        // Reload once for a clean start; cache now equals the server answer,
        // so the reload cannot loop. Cold starts have no previous value and
        // never reload.
        if (previous && previous !== tenantId) {
          globalThis.location.reload();
        }
      }
    } finally {
      clearTimeout(timer);
    }
  } catch (e) {
    // offline / network error / timeout: keep whatever is cached. Deferred:
    // a plain fetch killed by the page being replaced at boot rejects with the
    // same "Load failed" as a real outage, and a dying document never reaches
    // the timer.
    reportError(e, {
      severity: "WARNING",
      deferMs: TENANT_RESOLVE_REPORT_DEFER_MS,
      extra: { stage: "tenant-resolve" },
    });
  }
};

// Boot-time check used by main.tsx to decide between the app and the
// tenant-not-found screen. Non-browser environments never block.
export const hasResolvedTenantId = (): boolean => {
  try {
    // getStoredTenantId counts the cookie mirror too and restores the
    // localStorage copy when the LINE webview evicted it.
    return !!getStoredTenantId();
  } catch {
    return true;
  }
};
